<template>
  <BaseForm>
    <Flex
      padding
      direction="column"
    >
      <Flex
        v-if="hasFeatureFlag('compliant-register') && (isEditing || isPaying)"
        :gap=".5"
        v-test="'register-current-state'"
      >
        <BaseHeading v-if="order.state">{{ stateLabel }}</BaseHeading>
        <BaseLabel
          v-if="$screen === 'l' && order.state"
          :state="order.state?.toLowerCase()"
          >{{ t(`global.actions.${order.state?.toLowerCase()}`) }}</BaseLabel
        >
      </Flex>
      <Flex
        v-if="!cashupEnabled"
        direction="column"
      >
        <BaseDatePicker
          :label="t('global.date')"
          v-model="order.invoicedAt"
          v-test="'register-order-cashup-date'"
        />
      </Flex>
      <Flex
        v-if="!hasSingleEmployee"
        direction="column"
      >
        <BaseDropdown
          :label="t('global.employee')"
          v-model="selectedEmployeeId"
          :options="employeeList"
          :searchable="employeeList.length > 6"
          :required="!!order.items.length"
          :disabled="route.name === 'register-payment'"
          resources
          v-test="'register-order-employee-dropdown'"
        />
      </Flex>
      <Flex
        v-if="multiLocation && order.permissions?.editOrderItems"
        direction="column"
      >
        <BaseDropdown
          :label="t('global.items.location')"
          :modelValue="order.locationId"
          :options="
          locations.map((location) => ({
            value: location.id,
            label: location.internalName
          }))
        "
          @update:modelValue="switchLocation"
          v-test="'register-order-location'"
        />
      </Flex>
      <Customer
        :required="hasPrepaidCard"
        :customer="selectedCustomer"
        :ctaTextDefault="$t(`global.actions.add_to_${orderKey()}`)"
        :ctaTextCreate="$t(`global.actions.add_to_${orderKey()}_and_create`)"
        @submit="selectCustomer"
        @remove="selectCustomer"
        :error="!!v$.customerId.$errors.length"
        v-test="'sale-customer'"
      />
      <OrderLoyaltyPoints
        v-if="isLoyaltyPointsEnabled && order.customerId"
        v-test="'register-order-loyalty-points'"
      />
      <Flex
        v-if="order.note"
        direction="column"
        :gap="0"
      >
        <BaseInputLabel :text="t('global.items.note', 1)" />
        <OrderNote />
      </Flex>
      <Flex
        direction="column"
        v-if="hasFeatureFlag('fiskaly-sign-enabled') && hasFeatureFlag('compliant-register')"
        :gap=".25"
      >
        <BaseText
          size="s"
          color="secondary"
          v-test="'fiskaly-version'"
          v-if="fiskalyVersion"
        >
          {{ fiskalyVersion }}
        </BaseText>
        <BaseText
          size="s"
          color="secondary"
          v-test="'salonized-version'"
          v-if="salonizedVersion"
        >
          {{ t('integrations.fiskaly.salonized_version', brandName()) }}
          v{{ salonizedVersion }}
        </BaseText>
      </Flex>
    </Flex>
  </BaseForm>
</template>

<script
  lang="ts"
  setup
>
import useVuelidate from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, inject, nextTick, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import Customer from '@/components/customer/index.vue';
import Flex from '@/components/layout/Flex.vue';
import { brandName } from '@/helpers/brand-name';
import { orderKey } from '@/helpers/compliant-register';
import {
  type OrderItem,
  useRegisterOrderStore,
} from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useResourcesStore } from '@/stores/resources';
import { useUserStore } from '@/stores/user';
import type { Customer as CustomerType } from '@/types/graphql';
import OrderNote from './OrderNote.vue';
import OrderLoyaltyPoints from './order/OrderLoyaltyPoints.vue';

const route = useRoute();
const router = useRouter();
const { locations } = useLocationsStore();
const { isLoyaltyPointsEnabled, multiLocation } = useCompanyStore();
const { employees, hasSingleEmployee } = storeToRefs(useResourcesStore());
const { order } = useRegisterOrderStore();
const { selectedEmployeeId, selectedCustomer } = storeToRefs(
  useRegisterOrderStore(),
);
const { cashupEnabled, isEditing, isPaying } = storeToRefs(useRegisterStore());
const { exitCurrentState } = useRegisterStore();
const { hasFeatureFlag, fiskalyVersion, salonizedVersion } = storeToRefs(
  useUserStore(),
);

const { t } = useI18n();

const stateLabel = computed(() => {
  if (isEditing.value) {
    return t('global.actions.edit');
  }
  if (isPaying.value) {
    return t('global.actions.pay');
  }
});

const switchLocation = (locationId: number) => {
  router.replace({
    name: route.name as string,
    params: { locationId },
    query: route.query,
  });
};

const selectCustomer = (customer?: CustomerType) => {
  selectedCustomer.value = customer;
};

const employeeList = computed(() => {
  const list = employees.value.map((employee: any) => ({
    value: employee.id,
    label: employee.name,
  }));

  list.unshift({ value: 'multiple', label: t('global.multiple') });
  return list;
});

const hasPrepaidCard = computed(
  () => !!order.items.find((item: OrderItem) => item.prepaidCard),
);
const needsEmployee = computed(
  () => !hasSingleEmployee.value && !!order.items.length,
);

const rules = computed(() => ({
  selectedEmployeeId: { required: requiredIf(needsEmployee.value) },
  customerId: { required: requiredIf(hasPrepaidCard.value) },
}));

const v$ = useVuelidate(rules, {
  selectedEmployeeId,
  customerId: toRef(order, 'customerId'),
});

const registerSidebarTrigger = inject<
  (fn: (callback: (valid: boolean) => void) => void) => void
>('registerSidebarTrigger');

registerSidebarTrigger?.((callback) => {
  v$.value.$reset();
  nextTick(() => {
    v$.value.$touch();
    callback(!v$.value.$invalid);
  });
});

onBeforeRouteLeave((to) => {
  if (to.name !== 'register-payment' && to.name !== 'register-builder') {
    exitCurrentState();
  }
});
</script>
